import type { ReactElement } from 'react';
import { useState } from 'react';
import type { PaddingOrCallback, VictoryAxisProps } from 'victory';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { AreaGradient } from './components/AreaGradient';
import { DataLabel } from './components/DataLabel/DataLabel';
import { LineGraphFlyout } from './components/LineGraphFlyout/LineGraphFlyout';
import { VictoryWmgTheme } from './components/VictoryWmgTheme';

export type LineGraphData = {
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}[];

export type StreamType =
  | 'streams'
  | 'organicStreams'
  | 'programmedStreams'
  | 'benchmarkStreams';

interface LineGraphProps {
  streamType?: StreamType;
  data: LineGraphData[];
  lineColors?: string[];
  containerClassName?: string;
  xAxisProps?: VictoryAxisProps;
  yAxisProps?: VictoryAxisProps;
  tooltip?: ReactElement | undefined;
  tooltipPadding?: PaddingOrCallback;
  tooltipOffset?: number[];
  targetPercentage?: number;
  dataLabelText?: string;
}

function LineGraph({
  data,
  streamType,
  targetPercentage = 100,
  containerClassName = '',
  lineColors = ['#000000'],
  xAxisProps = {},
  yAxisProps = {},
  tooltip = undefined,
  tooltipPadding = 12,
  tooltipOffset = [0, 0],
  dataLabelText = 'Label',
}: LineGraphProps) {
  const [hovered, setHovered] = useState(false);
  const areaGradientIdPrefix = 'areaGradient_';

  const isBenchmarkStream = streamType === 'benchmarkStreams';

  return (
    <div className={containerClassName}>
      {lineColors.map((color, index) => (
        <AreaGradient
          key={index}
          id={areaGradientIdPrefix + index}
          lineColor={color}
        />
      ))}

      <VictoryChart
        padding={{
          top: 5,
          left: yAxisProps?.orientation === 'left' ? 40 : 0,
          right: yAxisProps?.orientation === 'left' ? 0 : 40,
          bottom: 15,
        }}
        height={isBenchmarkStream ? 200 : 100}
        domainPadding={10}
        theme={VictoryWmgTheme}
        containerComponent={
          isBenchmarkStream ? undefined : (
            <VictoryVoronoiContainer
              events={{
                onMouseOver: () => setHovered(true),
                onMouseOut: () => setHovered(false),
              }}
            />
          )
        }
      >
        {isBenchmarkStream ? (
          <DataLabel
            x={1}
            y={data[0][data[0].length - 1].y}
            text={[dataLabelText, `${targetPercentage}%`]}
          />
        ) : null}

        <VictoryAxis crossAxis {...xAxisProps} />
        <VictoryAxis dependentAxis {...yAxisProps} />
        {data.map((dataPoint, index) => {
          let dataType;

          if (streamType === 'organicStreams') {
            dataType = index === 0 ? 'library' : 'search';
          } else {
            dataType = 'streams';
          }

          return (
            <VictoryArea
              key={index}
              labelComponent={
                <VictoryTooltip
                  orientation="right"
                  flyoutComponent={
                    <LineGraphFlyout
                      streamType={streamType}
                      dataType={dataType}
                      data={data}
                      lineColors={lineColors}
                      customFlyoutComponent={tooltip}
                      customFlyoutOffset={tooltipOffset}
                    />
                  }
                  flyoutPadding={tooltipPadding}
                />
              }
              labels={({ datum }) => datum.label ?? ''}
              interpolation="natural"
              data={dataPoint}
              style={{
                data: {
                  stroke: lineColors[index],
                  fill: `url(#${areaGradientIdPrefix + index})`,
                  fillOpacity: hovered ? 100 : 0,
                  strokeDasharray:
                    isBenchmarkStream && index === 1 ? '2' : 'none',
                },
              }}
            />
          );
        })}
      </VictoryChart>
    </div>
  );
}

LineGraph.displayName = 'LineGraph';
export { LineGraph };
